<template>
  <section class="list-wrapper">
    <div class="header-wrapper">
      <div>
        <slot name="gal"></slot>
      </div>
      <div
        v-if="Array.isArray(sorting) && sorting.length"
        :style="{ visibility: isRequestPending || isRequestFailed ? 'hidden' : 'visible' }"
      >
        <label>Sort by:</label><yn-dropdown :value="sort" :options="sorting" @change="setSort"></yn-dropdown>
      </div>
      <div :style="{ visibility: isRequestPending || isRequestFailed ? 'hidden' : 'visible' }">
        <label>Total:</label>&nbsp;{{ total }}
      </div>
    </div>
    <ul class="list">
      <li :class="['list-item', 'header', 'li-' + _uid]">
        <div v-for="item of $$metadata" :key="item.name">{{ item.label }}</div>
        <div></div>
      </li>
    </ul>
    <div v-if="isRequestPending">
      <yn-loading :inline="true" height="200px"></yn-loading>
    </div>
    <div v-else-if="isRequestFailed">
      Failed to load data
    </div>
    <ul v-else class="list scrollable">
      <li v-for="row in $$data" :key="row.id" :class="['list-item', 'li-' + _uid]" @click="$emit('click', row)">
        <div v-for="(column, index) in $$metadata" :key="index">
          <slot :name="column.name" :value="row[column.name]" :item="row" :settings="column">
            <span>{{ row[column.name] }}</span>
          </slot>
        </div>

        <div class="list-item-actions-wrapper">
          <div class="list-item-actions">
            <slot name="__actions" :item="row">
              <yn-button
                v-for="action of actions"
                :key="action.name"
                variant="text"
                :title="action.tooltip"
                @click="$emit(action.name, row)"
              >
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="action.label"></span>
              </yn-button>
            </slot>
          </div>
        </div>
      </li>
    </ul>
    <div>
      <ul v-if="total > size && !isRequestPending && !isRequestFailed" class="pagination-list">
        <li class="pagination-list-item">
          <button :disabled="!(skip > 0)" @click.prevent="$emit('navigate', { skip: 0 })">
            <span>««</span>
          </button>
        </li>
        <li class="pagination-list-item">
          <button
            :disabled="!($$pagination.previous >= 0)"
            @click.prevent="$emit('navigate', { skip: $$pagination.previous })"
          >
            <span>« Previous</span>
          </button>
        </li>
        <li class="pagination-list-item">
          <span>Page {{ $$pagination.page + 1 }}</span>
        </li>
        <li class="pagination-list-item">
          <button
            :disabled="!($$pagination.next < total)"
            @click.prevent="$emit('navigate', { skip: $$pagination.next })"
          >
            <span>Next »</span>
          </button>
        </li>
        <li class="pagination-list-item">
          <button
            :disabled="!(skip < $$pagination.last)"
            @click.prevent="$emit('navigate', { skip: $$pagination.last })"
          >
            <span>»»</span>
          </button>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import Button from './Button';
import Dropdown from './Dropdown';
import Loading from './Loading';

export default {
  components: {
    'yn-button': Button,
    'yn-loading': Loading,
    'yn-dropdown': Dropdown
  },
  props: {
    name: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      required: true
    },
    total: {
      type: Number,
      required: true
    },
    skip: {
      type: Number,
      default: () => 0
    },
    size: {
      type: Number,
      default: () => 25
    },
    metadata: {
      type: Object,
      required: true
    },
    isRequestPending: {
      type: Boolean,
      dafault: () => false
    },
    isRequestFailed: {
      type: Boolean,
      dafault: () => false
    },
    actions: {
      type: Array,
      default: () => [
        { name: 'edit', label: '&#9998;', tooltip: 'Edit this item' },
        { name: 'delete', label: '&times;', tooltip: 'Delete this item' }
      ]
    },
    sort: {
      type: String,
      default: null
    },
    sorting: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {};
  },
  computed: {
    $$metadata() {
      return Object.keys(this.metadata).map(name => ({ name, ...this.metadata[name] }));
    },
    $$data() {
      return this.data;
    },
    $$pagination() {
      const obj = this;
      return {
        get page() {
          return Math.ceil(obj.skip / obj.size);
        },
        get previous() {
          return this.page * obj.size - obj.size;
        },
        get next() {
          return this.page * obj.size + obj.size;
        },
        get last() {
          return (Math.ceil(obj.total / obj.size) - 1) * obj.size;
        }
      };
    }
  },
  watch: {
    $$metadata: {
      handler() {
        const innerHTML = `
            .list-item.li-${this._uid} {
                grid-template-columns: ${this.$$metadata.reduce(
                  (result, current) => result + ' ' + current.width,
                  ''
                )} 100px !important;
            }
        `;

        const id = `list-item-${this._uid}`;
        if (document.getElementById(id) && document.getElementById(id).innerHTML === innerHTML) {
          return;
        }

        const el = document.createElement('style');
        el.setAttribute('id', id);
        el.innerHTML = innerHTML;
        document.head.appendChild(el);
      },
      deep: true,
      immediate: true
    }
  },
  destroyed() {
    const id = `list-item-${this._uid}`;
    this.$nextTick(() => {
      const el = document.getElementById(id);
      el.parentElement.removeChild(el);
    });
  },
  methods: {
    setSort(id) {
      if (this.sort === id) {
        return;
      }
      const option = this.sorting.find(option => option.id === id);
      if (!option) {
        return;
      }

      this.$emit('sort', id);
    }
  }
};
</script>

<style lang="scss" scoped>
.list-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: grid;
  grid-template-rows: minmax(0, max-content) minmax(0, max-content) minmax(0, 1fr) minmax(0, max-content);
  box-sizing: border-box;
  overflow: hidden;
  background-color: var(--theme-surface);
  position: relative;
  .header-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    font-size: 0.95rem;
    margin: 0;
    padding: 0;
    font-weight: 400;
    margin-right: 6px;
    padding-bottom: 1rem;
    line-height: 1;
    label {
      &:first-child {
        font-weight: 600;
        margin-right: 5px;
      }
    }

    > * {
      height: 100%;
      display: flex;
      align-items: center;

      &:not(:last-child) {
        margin-right: 2rem;
      }
    }

    .filter {
      > * {
        &:not(:last-child) {
          margin-right: 0.5rem;
        }
      }
    }
  }
  .list {
    margin: 0;
    padding: 0;
    list-style: none;

    .list-item {
      display: grid;
      //   grid-template-columns: 200px 300px minmax(0, 1fr) 175px 175px 100px;
      grid-gap: 0;
      border-bottom: 1px solid var(--theme-highlight);
      font-size: 1rem;
      min-height: 1.75rem;
      align-items: center;

      > * {
        border-left: 1px solid var(--theme-highlight);
        height: 100%;
        display: flex;
        align-items: center;
        padding: 0.5rem 0.75rem;
        text-overflow: ellipsis;
        word-break: break-word;
        white-space: pre-line;
        overflow: hidden;

        > label {
          overflow: hidden;
          text-overflow: ellipsis;
          display: inline-block;
          width: 100%;
        }

        &:last-child {
          border-left: 1px solid transparent;
          border-right: 1px solid var(--theme-highlight);
        }
      }

      &:first-child {
        border-top: 1px solid var(--theme-highlight);
      }

      &:hover {
        .list-item-actions {
          width: 100%;
          display: flex;
          justify-content: flex-end;

          & > * {
            &:not(:last-child) {
              margin-right: 0.25rem;
            }
          }
        }
      }

      &.header {
        font-size: 0.95rem;
        background-color: var(--theme-highlight);

        > * {
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          padding: 0.5rem 0.75rem;
          border-bottom: transparent;
          font-weight: 700;
        }
      }

      &:not(.header) {
        &:first-child {
          border-top: 0 none;
        }
      }
      .list-item-actions-wrapper {
        padding-top: 0;
        padding-bottom: 0;
      }
      .list-item-actions {
        display: none;
      }
    }

    &:not(:last-child) {
      // margin-right: 6px;
    }

    &.scrollable {
      overflow-y: auto;
      height: 100%;
      box-sizing: border-box;
    }
  }
}

.pagination-list {
  list-style: none;
  margin: 0.5rem 0;
  padding: 0;
  display: flex;

  align-items: center;

  .pagination-list-item {
    font-size: 0.8rem;

    &:not(:last-child) {
      margin-right: 1rem;
    }

    button {
      cursor: pointer;
      display: inline-block;
      border: 0 none;
      background: transparent;
      outline: none;
      color: var(--theme-primary);
      text-decoration: none;
      font-weight: 500;
      padding: 0;
      margin: 0;

      &.disabled,
      &[disabled] {
        opacity: 0.7;
        cursor: not-allowed;
        &:hover {
          color: var(--theme-primary);
        }
      }

      &:hover {
        color: var(--theme-primary-dark);
      }
    }

    span {
      font-size: 0.75rem;
      display: inline-block;
      vertical-align: sub;
      line-height: 1;
    }
  }

  i {
    font-style: italic;
  }
}
</style>
